interface DualLabelProps {
  /** English primary text — always legible chrome for a learner who can't yet read Devanagari. */
  en: string;
  /** Optional small Devanagari echo beneath, for passive exposure. Never letter-spaced. */
  hi?: string;
  className?: string;
  /**
   * "stack" (default) — en above, hi echo beneath, for headings/blocks.
   * "inline" — compact single-line pairing for buttons and other tight
   * contexts: en then a small hi echo trailing on the same line.
   */
  variant?: "stack" | "inline";
}

/**
 * DualLabel — the shared bilingual chrome primitive.
 *
 * The app teaches Devanagari to fluent Hindi speakers who cannot yet read
 * it, so all UI CHROME (buttons, nav, prompts, headers) must be operable in
 * English, with a small Devanagari echo for passive exposure. Content being
 * taught (glyphs, example words, reading targets) never goes through this
 * component — it stays pure Devanagari.
 *
 * The Devanagari echo always carries `lang="hi"` and is never letter-spaced
 * (tracking breaks conjunct/matra shaping — see `.mistakes/LEDGER.md`).
 */
export function DualLabel({
  en,
  hi,
  className = "",
  variant = "stack",
}: DualLabelProps) {
  // The echo inherits `currentColor` (not a fixed ink tone) at reduced
  // opacity, so it stays legible-but-quiet against whatever background it
  // sits on — paper text (dark-on-light) as well as solid ink/sindoor
  // buttons (light-on-dark), where a hardcoded dark tone would nearly
  // vanish into the button fill.
  if (variant === "inline") {
    return (
      <span className={`inline-flex items-baseline gap-1.5 ${className}`}>
        <span lang="en">{en}</span>
        {hi !== undefined && (
          <span
            lang="hi"
            className="font-dev text-[0.7em] text-current opacity-70"
          >
            {hi}
          </span>
        )}
      </span>
    );
  }

  return (
    <span className={`inline-flex flex-col ${className}`}>
      <span lang="en">{en}</span>
      {hi !== undefined && (
        <span
          lang="hi"
          className="mt-0.5 font-dev text-[0.7em] leading-tight text-current opacity-70"
        >
          {hi}
        </span>
      )}
    </span>
  );
}
